(function () {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const sections = Array.from(document.querySelectorAll('.home-hero, .home-section'))
    .filter((el) => !el.classList.contains('home-trust'));

  if (sections.length < 2) return;

  // Only enable snap when the user is actually within the home sections.
  // (Once they're in the footer, normal scrolling takes over.)
  function isInHomeArea() {
    const last = sections[sections.length - 1];
    const lastBottom = last.getBoundingClientRect().bottom + window.scrollY;
    return window.scrollY < lastBottom - 50;
  }

  // Tunables
  const ANIM_DURATION = 180;
  const GESTURE_LOCK_BUFFER = 100;
  const TOUCH_THRESHOLD = 30;
  const WHEEL_MIN_DELTA = 4;

  let isAnimating = false;
  let animationFrame = null;
  let gestureLockUntil = 0;

  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function getSectionTops() {
    return sections.map((s) => Math.round(s.getBoundingClientRect().top + window.scrollY));
  }

  function getCurrentIndex() {
    const scrollY = window.scrollY;
    const tops = getSectionTops();
    let idx = 0;
    for (let i = 0; i < tops.length; i++) {
      if (tops[i] <= scrollY + 5) idx = i;
      else break;
    }
    return idx;
  }

  function animateScrollTo(targetY) {
    if (animationFrame) cancelAnimationFrame(animationFrame);
    const startY = window.scrollY;
    const distance = targetY - startY;
    if (distance === 0) return;
    const startTime = performance.now();
    isAnimating = true;

    function step(now) {
      const progress = Math.min((now - startTime) / ANIM_DURATION, 1);
      const eased = easeOutCubic(progress);
      window.scrollTo(0, Math.round(startY + distance * eased));
      if (progress < 1) {
        animationFrame = requestAnimationFrame(step);
      } else {
        animationFrame = null;
        isAnimating = false;
      }
    }
    animationFrame = requestAnimationFrame(step);
  }

  function slide(direction) {
    const now = performance.now();
    if (isAnimating) return false;
    if (now < gestureLockUntil) return false;

    const tops = getSectionTops();
    const current = getCurrentIndex();
    const scrollY = window.scrollY;
    const offsetIntoCurrent = scrollY - tops[current];

    let target;
    if (direction > 0) {
      target = current + 1;
    } else {
      if (offsetIntoCurrent > 50) {
        target = current;
      } else {
        target = current - 1;
      }
    }

    if (target < 0 || target >= tops.length) return false;

    gestureLockUntil = now + ANIM_DURATION + GESTURE_LOCK_BUFFER;
    animateScrollTo(tops[target]);
    return true;
  }

  // ===== Wheel =====
  function onWheel(e) {
    if (!isInHomeArea()) return; // already in footer area → native scroll

    const tops = getSectionTops();
    const currentIdx = getCurrentIndex();

    // At edge of home sections AND scrolling outward → let native scroll take over
    if (e.deltaY > 0 && currentIdx >= tops.length - 1) return; // last section, scrolling down → footer
    if (e.deltaY < 0 && currentIdx <= 0 && window.scrollY <= 5) return; // first section top, scrolling up

    e.preventDefault();

    const now = performance.now();
    if (isAnimating || now < gestureLockUntil) return;
    if (Math.abs(e.deltaY) < WHEEL_MIN_DELTA) return;

    slide(e.deltaY > 0 ? 1 : -1);
  }

  window.addEventListener('wheel', onWheel, { passive: false });

  // ===== Touch =====
  let touchStartY = null;
  let touchValid = false;
  let slideFiredThisGesture = false; // hard guard: only ONE slide per gesture

  function resetTouchState() {
    touchStartY = null;
    touchValid = false;
    slideFiredThisGesture = false;
  }

  window.addEventListener('touchstart', (e) => {
    resetTouchState();
    if (!isInHomeArea()) return;
    const now = performance.now();
    if (isAnimating || now < gestureLockUntil) return;
    touchStartY = e.touches[0].clientY;
    touchValid = true;
  }, { passive: true });

  window.addEventListener('touchmove', (e) => {
    if (!isInHomeArea()) return;
    if (!touchValid || isAnimating) return;
    if (touchStartY === null) return;
    if (slideFiredThisGesture) return;        // HARD GUARD: gesture already triggered a slide

    const currentY = e.touches[0].clientY;
    const deltaY = touchStartY - currentY;
    const swipingUp = deltaY > 0;
    const swipingDown = deltaY < 0;

    const tops = getSectionTops();
    const currentIdx = getCurrentIndex();

    if (swipingUp && currentIdx >= tops.length - 1) { touchValid = false; return; }
    if (swipingDown && currentIdx <= 0 && window.scrollY <= 5) { touchValid = false; return; }

    e.preventDefault();

    if (Math.abs(deltaY) >= TOUCH_THRESHOLD) {
      slideFiredThisGesture = true;  // mark IMMEDIATELY before calling slide
      touchValid = false;
      touchStartY = null;
      slide(swipingUp ? 1 : -1);
    }
  }, { passive: false });

  window.addEventListener('touchend', (e) => {
    if (!isInHomeArea()) { resetTouchState(); return; }
    if (slideFiredThisGesture) { resetTouchState(); return; } // already fired during move
    if (!touchValid || touchStartY === null) { resetTouchState(); return; }
    const now = performance.now();
    if (isAnimating || now < gestureLockUntil) { resetTouchState(); return; }

    const endY = e.changedTouches[0].clientY;
    const deltaY = touchStartY - endY;
    resetTouchState();

    if (Math.abs(deltaY) < TOUCH_THRESHOLD) return;
    slide(deltaY > 0 ? 1 : -1);
  }, { passive: true });

  window.addEventListener('touchcancel', resetTouchState, { passive: true });

  // ===== Viewport / focus loss safeguards =====
  // If the pointer leaves the page or the window loses focus mid-gesture,
  // wipe touch state so the next gesture starts fresh.
  window.addEventListener('blur', resetTouchState);
  window.addEventListener('mouseleave', resetTouchState);
  document.addEventListener('mouseleave', resetTouchState);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) resetTouchState();
  });

  // ===== Keyboard =====
  window.addEventListener('keydown', (e) => {
    if (!isInHomeArea()) return;
    const now = performance.now();
    if (isAnimating || now < gestureLockUntil) return;
    let direction = 0;
    if (['ArrowDown', 'PageDown', ' '].includes(e.key)) direction = 1;
    else if (['ArrowUp', 'PageUp'].includes(e.key)) direction = -1;
    else return;
    if (slide(direction)) e.preventDefault();
  });
})();
